/**
 * The four surfaces this library renders, declared here rather than imported
 * from the generated admin client so a presentational library keeps depending on
 * nothing but React and the design system.
 *
 * Hand-writing it is only safe because two checks stand behind it.
 * `PLACEMENT_CAPABILITIES` is typed against this union, so a cell naming a surface
 * with no renderer does not compile; and admin-web asserts that the generated
 * writable-placement enum has no member missing from here, so a backend that adds
 * or renames a placement fails to build rather than reaching the form as a surface
 * that supports nothing.
 */
export type AnnouncementRenderedPlacement =
  | 'dashboard_banner'
  | 'modal'
  | 'strip_banner'
  | 'toaster'

/**
 * A field whose value only SOME of those surfaces read.
 *
 * A modal authors its copy and buttons through blocks, so the flat content
 * and CTA controls belong only to the other placements.
 */
export type AnnouncementPlacementCapability =
  | 'autoClose'
  | 'blocks'
  | 'body'
  | 'bodyStyle'
  | 'countdown'
  | 'primaryCta'
  | 'dismissible'
  | 'image'
  | 'pacingGap'
  | 'secondaryCta'
  | 'title'
  | 'titleHighlight'
  | 'titleSize'
  | 'toasterMediaLayout'
  | 'toasterNewLabel'
  | 'video'

type PlacementCapability = {
  /** The placements whose renderer reads the field. */
  readonly placements: readonly AnnouncementRenderedPlacement[]
  /**
   * Why the field does nothing on the others.
   *
   * Only `image` and `video` reach a screen today, in the admin's warning for a
   * staged file the placement will silently drop; the form shows no reason beside
   * a control, because it renders no inert controls at all. The other eight
   * document their own matrix cell for whoever reads it next, and are required
   * rather than optional so a cell can never be added without stating why the
   * field is inert there.
   */
  readonly inertReason: string
}

/**
 * Which placement reads which field — the one source deciding which controls the
 * admin form renders AND which rules its validation applies, so the two can never
 * drift apart.
 *
 * It lives beside the components rather than in admin-web because a wrong cell is
 * silent: the form omits a control it believes inert, so a field a customer
 * renderer really does read becomes one no operator can see or set, with nothing
 * on the page hinting it exists. `placement-capabilities.test.ts` ties the five
 * cells this library's own prop surface can express back to the components, and
 * records why the other five cannot be checked here.
 *
 * Ground truth for every cell remains the mappers in
 * `apps/gto-wizard-web/src/features/announcements/`, read field by field. Read
 * them for what reaches a screen, not for where the identifier appears: the strip
 * banner names `body_style` only to report a diagnostic, and hardcodes `plain`
 * for what it actually renders, so it does not support `bodyStyle`.
 *
 * Strictly a READS matrix: whether a surface reads a field, never whether it looks
 * unfinished without one.
 *
 * Declared `as const` so each row stays a literal tuple — that is what lets the
 * tie test compare a cell against a component's props at compile time instead of
 * against a second hand-written copy of the matrix.
 */
export const PLACEMENT_CAPABILITIES = {
  primaryCta: {
    placements: ['toaster', 'strip_banner', 'dashboard_banner'],
    inertReason: 'A modal authors buttons through its content blocks.',
  },
  title: {
    placements: ['toaster', 'strip_banner', 'dashboard_banner'],
    inertReason: 'A modal authors its visible heading and accessible name through blocks.',
  },
  blocks: {
    placements: ['modal'],
    inertReason: 'Modals only — every other surface renders the flat title and body above.',
  },
  body: {
    placements: ['toaster', 'strip_banner', 'dashboard_banner'],
    inertReason: 'A modal authors its copy through blocks and never reads the flat body.',
  },
  titleHighlight: {
    placements: ['strip_banner', 'toaster'],
    inertReason:
      'The dashboard slide and the modal paint their own headline, so neither reads an accent phrase.',
  },
  toasterMediaLayout: {
    placements: ['toaster'],
    inertReason:
      'Toasters only — no other surface lets its media reach the card edges, and the strip bar draws none at all.',
  },
  toasterNewLabel: {
    placements: ['toaster'],
    inertReason: 'Toasters only — no other surface draws the "New" chip.',
  },
  titleSize: {
    placements: ['dashboard_banner'],
    inertReason: 'Dashboard slides only — every other surface sizes its headline itself.',
  },
  countdown: {
    placements: ['dashboard_banner'],
    inertReason: 'Dashboard slides only — no other surface has room for the tiles.',
  },
  bodyStyle: {
    placements: ['toaster', 'dashboard_banner'],
    inertReason: 'The strip bar renders the body as one plain line whatever this says.',
  },
  image: {
    placements: ['toaster', 'dashboard_banner'],
    inertReason: 'The strip bar is a text-only bar — it renders no media.',
  },
  video: {
    placements: ['toaster'],
    inertReason: 'Toasters only — no other surface plays video.',
  },
  secondaryCta: {
    placements: ['toaster', 'dashboard_banner'],
    inertReason: 'The strip bar renders one button only.',
  },
  autoClose: {
    placements: ['toaster'],
    inertReason: 'Toasters only — no other surface closes itself on a timer.',
  },
  dismissible: {
    placements: ['toaster', 'strip_banner', 'modal'],
    inertReason: 'The dashboard carousel has no close control, so a slide is never dismissible.',
  },
  pacingGap: {
    placements: ['toaster', 'strip_banner', 'modal'],
    inertReason:
      'A pacing gap is measured from a close, and the dashboard carousel has no close control to measure from. The backend rejects one on a slide.',
  },
} as const satisfies Record<AnnouncementPlacementCapability, PlacementCapability>
